import _IconClose from "@arco-design/web-react/icon/react-icon/IconClose";
import "@arco-design/web-react/es/Upload/style";
import _Upload from "@arco-design/web-react/es/Upload";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import _IconUpload from "@arco-design/web-react/icon/react-icon/IconUpload";
import _IconDownload from "@arco-design/web-react/icon/react-icon/IconDownload";
import "@arco-design/web-react/es/Tooltip/style";
import _Tooltip from "@arco-design/web-react/es/Tooltip";
import "@arco-design/web-react/es/Progress/style";
import _Progress from "@arco-design/web-react/es/Progress";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import "@arco-design/web-react/es/Notification/style";
import _Notification from "@arco-design/web-react/es/Notification";
import "@arco-design/web-react/es/Grid/style";
import _Grid from "@arco-design/web-react/es/Grid";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import React, { useEffect, useRef, useState } from 'react';
import { PdfIcon2, WordIcon, ExcelIcon, PPTIcon, PictureIcon, ZipIcon, TXTIcon, UnknownIcon } from '@/components/Icons/File';
import { useRequest } from 'ahooks';
import { prefix } from '@/utils/prefix';
import { storageGetItem } from '@/utils/storage';
import { deleteFile } from '@/services/storageService';
import { useModal } from '@/context/ModalContext';
import './index.less';
const {
  Row,
  Col
} = _Grid;

/** 字节数转可读字符串（KB/MB/GB） */
function transform(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
}

// 默认 accept 字符串（拼接办公文档 + 压缩包 + 图片三大类）
const DEFAULT_ACCEPT = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.md,.csv,.zip,.rar,.7z,.tar,.gz,.jpg,.jpeg,.png,.gif,.bmp,.webp,.html,.js,.css,.json';

// 默认文件图标（SVG，来自 @/components/Icons/File）。
// key 是文件后缀（不带点的小写形式）。可用 <FilesUpload fileIcon={fn} /> 替换。
const DEFAULT_FILE_ICONS = {
  // 文档
  pdf: /*#__PURE__*/React.createElement(PdfIcon2, null),
  doc: /*#__PURE__*/React.createElement(WordIcon, null),
  docx: /*#__PURE__*/React.createElement(WordIcon, null),
  txt: /*#__PURE__*/React.createElement(TXTIcon, null),
  // 表格
  xls: /*#__PURE__*/React.createElement(ExcelIcon, null),
  xlsx: /*#__PURE__*/React.createElement(ExcelIcon, null),
  csv: /*#__PURE__*/React.createElement(ExcelIcon, null),
  // 演示
  ppt: /*#__PURE__*/React.createElement(PPTIcon, null),
  pptx: /*#__PURE__*/React.createElement(PPTIcon, null),
  // 图片
  jpg: /*#__PURE__*/React.createElement(PictureIcon, null),
  jpeg: /*#__PURE__*/React.createElement(PictureIcon, null),
  png: /*#__PURE__*/React.createElement(PictureIcon, null),
  gif: /*#__PURE__*/React.createElement(PictureIcon, null),
  bmp: /*#__PURE__*/React.createElement(PictureIcon, null),
  webp: /*#__PURE__*/React.createElement(PictureIcon, null),
  svg: /*#__PURE__*/React.createElement(PictureIcon, null),
  // 压缩包
  zip: /*#__PURE__*/React.createElement(ZipIcon, null),
  rar: /*#__PURE__*/React.createElement(ZipIcon, null),
  '7z': /*#__PURE__*/React.createElement(ZipIcon, null),
  tar: /*#__PURE__*/React.createElement(ZipIcon, null),
  gz: /*#__PURE__*/React.createElement(ZipIcon, null)
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeFileItem(item) {
  // 兼容后端字段（id/originalName/suffix）和组件内部字段（id/fileName/ext）
  const id = item.id || item.uid;
  const fileName = item.fileName || item.originalName || item.name;
  return {
    ...item,
    localUid: item.localUid || id || item.uuid || fileName || item.name,
    name: item.name || fileName,
    ext: item.ext || item.suffix || (fileName || item.name)?.split?.('.')?.pop?.(),
    size: item.size,
    id,
    uuid: item.uuid || id,
    fileName,
    percent: item.percent ?? 100,
    status: item.status || 'done'
  };
}
function mergeFileList(prev, nextList) {
  const normalizedNext = nextList.map(normalizeFileItem);
  const nextIdSet = new Set(normalizedNext.map(item => item.id || item.uuid).filter(Boolean));
  const pendingList = prev.filter(item => {
    if (item.status !== 'uploading') return false;
    const id = item.id || item.uuid;
    return !id || !nextIdSet.has(id);
  });
  return [...pendingList, ...normalizedNext];
}
function sortFileList(list) {
  if (Array.isArray(list)) {
    return [...list].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  }
  return [];
}
function emitDoneFileIds(list, onChange) {
  onChange?.(list.filter(item => item.status === 'done' && item.id).map(item => item.id).join(','));
}
export function getFileIcon(ext, custom) {
  if (custom) return custom(ext);
  if (!ext) return /*#__PURE__*/React.createElement(UnknownIcon, null);
  // 兼容后端 suffix（带点 .pdf）和前端 File.name 拆分结果（不带点 pdf）
  const key = ext.toLowerCase().replace(/^\./, '');
  return DEFAULT_FILE_ICONS[key] || /*#__PURE__*/React.createElement(UnknownIcon, null);
}

// ============================================================================
// service 包装 —— 项目里 storageService.uploadFile / downloadFile 没进度回调，
// 这里用 XHR 自己实现，调用同一个 `${prefix.storage}/file/...` 接口。
// ============================================================================

/**
 * 上传文件 —— 用裸 XHR，直接 POST multipart/form-data。
 * 走和项目里 storageService.uploadFile 同样的接口路径：
 *   ${prefix.storage}/file/upload
 * 自动带 Authorization（和 axios 拦截器保持一致），不依赖 axios 行为。
 */
function uploadFile(file, onProgress, onCancel) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    onCancel(() => xhr.abort());
    const formData = new FormData();
    formData.append('file', file);
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) {
        onProgress(Math.round(e.loaded / e.total * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText || '{}');
          // 兼容两种响应壳：{ code, data: {...} } 或 {...} 直接平铺
          const inner = data?.data ?? data;
          const fileName = inner.originalName || inner.storageName || file.name;
          const ext = inner.suffix || (fileName || '').split('.').pop() || '';
          const id = inner.id;
          if (!id) {
            reject(new Error('上传响应缺少 id: ' + xhr.responseText));
            return;
          }
          resolve({
            id,
            fileName,
            ext,
            size: inner.size ?? file.size
          });
        } catch (err) {
          reject(new Error('解析上传响应失败: ' + err.message));
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => {
      reject(new Error('网络错误'));
    };
    xhr.onabort = () => reject(new Error('上传已取消'));
    xhr.open('POST', `${prefix.storage}/file/upload`, true);
    const token = storageGetItem('token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

/**
 * 按 id 列表拉文件详情 —— 用 fetch，依赖后端返回结构。
 * 后端期望响应：ApiResponse<FileEntity[]> 或 FileEntity[]。
 */
async function getFileListById(ids) {
  const token = storageGetItem('token');
  const res = await fetch(`${prefix.storage}/file/list?ids=${ids}`, {
    headers: token ? {
      Authorization: `Bearer ${token}`
    } : {},
    credentials: 'include'
  });
  if (!res.ok) return [];
  const json = await res.json();
  const list = json?.data ?? json;
  return Array.isArray(list) ? list : [];
}

/**
 * 下载文件 —— 流式 fetch + ReadableStream，拿到长度 + 已读字节算进度。
 * 下载完成后从 content-disposition 拿文件名，写到本地。
 */
async function fileDownloadWithProgress(id, onProgress, onCancel, fallbackFilename) {
  const controller = new AbortController();
  onCancel(() => controller.abort());
  const token = storageGetItem('token');
  const res = await fetch(`${prefix.storage}/file/${id}/download`, {
    headers: token ? {
      Authorization: `Bearer ${token}`
    } : {},
    credentials: 'include',
    signal: controller.signal
  });
  if (!res.ok) {
    throw new Error(`下载失败 HTTP ${res.status}`);
  }
  const total = Number(res.headers.get('content-length') || 0);
  const reader = res.body?.getReader();
  const chunks = [];
  let loaded = 0;
  if (!reader) {
    // 兜底：浏览器不支持流式读取
    const blob = await res.blob();
    return finalizeDownload(blob, fallbackFilename);
  }
  while (true) {
    const {
      done,
      value
    } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      if (total > 0) {
        onProgress(Math.min(Math.round(loaded / total * 100), 99));
      }
    }
  }
  onProgress(100);
  const blob = new Blob(chunks);
  return finalizeDownload(blob, fallbackFilename);
}
function finalizeDownload(blob, filename) {
  const disposition = blob;
  void disposition;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
  return {
    filename
  };
}

// ============================================================================
// 组件
// ============================================================================

export function FilesUpload({
  isStatic,
  value,
  onChange,
  accept,
  fileIcon
}) {
  const [fileList, setFileList] = useState([]);
  const fileListRef = useRef([]);
  const {
    openFileViewer
  } = useModal();
  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);
  const [notification, notificationContextHolder] = _Notification.useNotification({
    maxCount: 1
  });
  const applyFileList = (nextList, options) => {
    fileListRef.current = nextList;
    setFileList(nextList);
    if (options?.emit !== false) {
      emitDoneFileIds(nextList, onChange);
    }
  };
  const updateFileByLocalUid = (localUid, updater, options) => {
    applyFileList(fileListRef.current.map(item => item.localUid === localUid ? updater(item) : item), options);
  };
  const removeFileAtIndex = (index, options) => {
    applyFileList(fileListRef.current.filter((_, i) => i !== index), options);
  };
  const removeFileByLocalUid = (localUid, options) => {
    applyFileList(fileListRef.current.filter(item => item.localUid !== localUid), options);
  };
  const getFileListByIdApi = useRequest(getFileListById, {
    manual: true,
    onSuccess: list => {
      applyFileList(mergeFileList(fileListRef.current, list || []), {
        emit: false
      });
    },
    onError: err => {
      console.error('获取文件列表失败:', err);
    }
  });
  const onUploadFile = file => {
    const localUid = `${file?.name || 'file'}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const exists = fileListRef.current.some(item => item.localUid === localUid);
    if (!exists) {
      applyFileList([...fileListRef.current, {
        localUid,
        name: file?.name,
        ext: file?.name?.split?.('.')?.pop?.(),
        size: file?.size,
        percent: 0,
        status: 'uploading'
      }],
      // 上传中的临时项不立刻回写表单值（避免外部 value 同步把首传清掉）
      {
        emit: false
      });
    }
    uploadFile(file, percent => {
      updateFileByLocalUid(localUid, item => ({
        ...item,
        percent
      }), {
        emit: false
      });
    }, cancel => {
      updateFileByLocalUid(localUid, item => ({
        ...item,
        cancel
      }), {
        emit: false
      });
    }).then(data => {
      if (!data.id) return;
      updateFileByLocalUid(localUid, item => ({
        ...item,
        name: data.fileName,
        fileName: data.fileName,
        ext: data.ext,
        size: data.size,
        id: data.id,
        uuid: data.id,
        percent: 100,
        status: 'done'
      }));
    }).catch(error => {
      console.error('上传文件失败:', error);
      _Message.error(error?.message || '上传文件失败');
      removeFileByLocalUid(localUid, {
        emit: false
      });
    });
  };
  function removeFile(event, index, item) {
    event.stopPropagation();
    event.preventDefault();
    if (item?.status === 'uploading') {
      item?.cancel?.();
      _Message.warning(`${item.name}上传已取消`);
      removeFileAtIndex(index);
      return;
    }
    if (item?.status === 'done') {
      // 项目里 storageService.deleteFile 的签名是 (id: string) => Promise<void>
      const id = item.id;
      if (!id) return;
      deleteFile(id).then(() => {
        _Message.success(`${item.name}删除成功`);
        removeFileAtIndex(index);
      });
      return;
    }
    removeFileAtIndex(index);
  }
  function downloadFile(event, item) {
    event.stopPropagation();
    event.preventDefault();
    if (!item?.id) return;
    const key = `download_${item.id}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const filename = item.name || item.fileName || '文件';
    let downloadCancel;
    let finished = false;
    let cancelled = false;
    const openDownloadingNotification = percent => {
      notification.info({
        id: key,
        title: '下载中',
        content: /*#__PURE__*/React.createElement("div", {
          className: "lx-download-row"
        }, /*#__PURE__*/React.createElement("div", {
          className: "lx-download-name"
        }, filename), /*#__PURE__*/React.createElement(_Progress, {
          type: "circle",
          percent: percent,
          size: "mini",
          strokeWidth: 10,
          formatText: value => /*#__PURE__*/React.createElement("span", {
            style: {
              color: '#000',
              fontSize: 12
            }
          }, value, "%")
        })),
        duration: 0,
        onClose: () => {
          if (!finished && !cancelled && downloadCancel) {
            cancelled = true;
            finished = true;
            downloadCancel();
            _Message.warning(`${filename}下载已取消`);
          }
        }
      });
    };
    openDownloadingNotification(0);
    fileDownloadWithProgress(item.id, percent => {
      openDownloadingNotification(percent);
    }, cancel => {
      downloadCancel = cancel;
    }, filename).then(({
      filename: realFilename
    }) => {
      finished = true;
      notification.info({
        id: key,
        title: '下载完成',
        content: `${realFilename} 100%`,
        duration: 2
      });
    }).catch(error => {
      if (cancelled) return;
      finished = true;
      notification.info({
        id: key,
        title: '下载失败',
        content: error?.message || `${filename} 下载失败`,
        duration: 2
      });
    });
  }
  function onViewFile(event, item) {
    event.stopPropagation();
    event.preventDefault();
    if (item.id) {
      // 调用项目里 ModalContext.openFileViewer 触发文件预览弹窗
      openFileViewer({
        url: `${prefix.storage}/file/${item.id}/download`,
        filename: item.fileName || item.name || '文件'
      });
    }
  }
  useEffect(() => {
    if (!value) {
      applyFileList([], {
        emit: false
      });
      return;
    }
    if (Array.isArray(value)) {
      applyFileList(mergeFileList(fileListRef.current, value), {
        emit: false
      });
      return;
    }
    getFileListByIdApi.run(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // ============================================================
  // 渲染：isStatic 模式（只读）
  // ============================================================
  if (isStatic) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, notificationContextHolder, /*#__PURE__*/React.createElement("div", {
      className: "lx-upload-list lx-upload-static"
    }, sortFileList(fileList).map((item, index) => {
      const {
        size,
        name
      } = item || {};
      const fileSize = transform(size || 0);
      return /*#__PURE__*/React.createElement(Row, {
        className: "lx-upload-row",
        key: index,
        onClick: event => onViewFile(event, item)
      }, /*#__PURE__*/React.createElement(Col, {
        flex: "25px"
      }, /*#__PURE__*/React.createElement("span", {
        className: "lx-upload-sort"
      }, index + 1), /*#__PURE__*/React.createElement("span", {
        className: "lx-file-title"
      }, getFileIcon(item.ext, fileIcon))), /*#__PURE__*/React.createElement(Col, {
        flex: "auto"
      }, /*#__PURE__*/React.createElement("div", {
        className: "lx-file-info"
      }, /*#__PURE__*/React.createElement(_Tooltip, {
        position: "top",
        content: name
      }, /*#__PURE__*/React.createElement("div", null, name)), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null, fileSize), /*#__PURE__*/React.createElement("span", {
        onClick: event => downloadFile(event, item)
      }, /*#__PURE__*/React.createElement(_IconDownload, null))))));
    })));
  }

  // ============================================================
  // 渲染：可上传
  // ============================================================
  return /*#__PURE__*/React.createElement(React.Fragment, null, notificationContextHolder, /*#__PURE__*/React.createElement("div", {
    className: "lx-files-upload"
  }, /*#__PURE__*/React.createElement(_Upload, {
    multiple: true,
    autoUpload: true,
    accept: accept || DEFAULT_ACCEPT,
    fileList: fileList,
    onChange: nextList => {
      // 由 onUploadFile 接管文件列表生命周期，onChange 不主动 push
      void nextList;
    },
    customRequest: options => {
      onUploadFile(options.file);
      return {
        abort: () => {
          /* 真 abort 路径在 removeFile -> item.cancel?.() */
        }
      };
    },
    beforeUpload: () => true,
    showUploadList: false
  }, /*#__PURE__*/React.createElement(_Button, {
    icon: /*#__PURE__*/React.createElement(_IconUpload, null)
  }, "\u70B9\u51FB\u4E0A\u4F20")), /*#__PURE__*/React.createElement("div", {
    className: "lx-upload-list"
  }, sortFileList(fileList).map((item, index) => {
    const {
      size,
      name
    } = item || {};
    const fileSize = transform(size || 0);
    return /*#__PURE__*/React.createElement(Row, {
      className: "lx-upload-row",
      style: {
        pointerEvents: 'all'
      },
      key: index,
      onClick: event => onViewFile(event, item)
    }, /*#__PURE__*/React.createElement(Col, {
      flex: "25px"
    }, /*#__PURE__*/React.createElement("span", {
      className: "lx-upload-sort"
    }, index + 1), /*#__PURE__*/React.createElement("span", {
      className: "lx-file-title"
    }, getFileIcon(item.ext, fileIcon))), /*#__PURE__*/React.createElement(Col, {
      flex: "auto"
    }, /*#__PURE__*/React.createElement("div", {
      className: "lx-file-info"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null, name), item.status === 'uploading' && /*#__PURE__*/React.createElement("span", {
      style: {
        marginLeft: 6,
        marginRight: 6,
        fontSize: 12,
        color: '#108ee9'
      }
    }, item.percent || 0, "%"), /*#__PURE__*/React.createElement("span", {
      onClick: event => removeFile(event, index, item)
    }, /*#__PURE__*/React.createElement(_IconClose, null))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", null, fileSize), item.id && /*#__PURE__*/React.createElement("span", {
      onClick: event => downloadFile(event, item)
    }, /*#__PURE__*/React.createElement(_IconDownload, null))))));
  }))));
}
export default FilesUpload;